import React from 'react';

import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import SidePanel from 'src/components/sidePanel/SidePanel';
import { cardMessages } from 'src/features/cards/utils/messages';
import { useListPoints } from 'src/features/points/api/useListPoints';
import { useListManualPointOpensStatistics } from 'src/features/statistics/api/useListManualPointOpens';
import { useListUsers } from 'src/features/statistics/api/useListUsers';
import { CustomDataTableControls } from 'src/features/statistics/components/CustomDataTableControls';
import { OperationManualPointOpensSidePanel } from 'src/features/statistics/components/OperationManualPointOpensSidePanel';
import { statisticsMessages } from 'src/features/statistics/utils';
import useFilters from 'src/hooks/useFilters';
import usePagination from 'src/hooks/usePagination';
import useSearch from 'src/hooks/useSearch';
import useSorting from 'src/hooks/useSorting';
import { useParking } from 'src/providers/ParkingProvider';
import {
    SectionBlock,
    SectionBlockHead,
    SectionBlockHeader,
    SectionBlockSeparator,
    SectionBlockSubtitle,
    SectionBlockTitle,
} from 'src/registry/components/blocks/SectionBlock';
import { CellDatetime, DataTable, DataTableColumnHeader, DataTablePagination } from 'src/registry/components/table';
import DataTableDateDropdown from 'src/registry/components/table/DataTableDateDropdown';
import useRowSelection from 'src/registry/hooks/useRowSelection';
import { Button } from 'src/registry/ui/Button';
import { useTitleStore } from 'src/stores/useTitleStore';

import type { StatisticManualPointOpens } from 'src/features/statistics/types';

export default function OperationManualPointOpens() {
    const { parkingId } = useParking();

    const { setTitle, removeTitle } = useTitleStore();
    const [dateTime, setDateTime] = React.useState(() => {
        const currentDate = new Date();
        const today = new Date(currentDate.getTime());
        today.setHours(23);
        today.setMinutes(59);
        today.setSeconds(59);
        const lastWeek = new Date(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7).getTime());
        return { afterDate: lastWeek, beforeDate: today };
    });

    React.useEffect(() => {
        setTitle(formatMessage(statisticsMessages.manualPointActionOperations));
        return () => removeTitle();
    }, []);

    const { data: users } = useListUsers({ parkingId });
    const { data: pointData } = useListPoints({ parkingId });

    const { formatMessage } = useIntl();

    const MANUAL_POINT_OPENS_PANEL_NAME = 'manual-point-opens-panel';

    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'enter_datetime', desc: false }],
    });
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'none' });
    const { clearRowSelection, ...rowSelection } = useRowSelection({ sidePanelName: MANUAL_POINT_OPENS_PANEL_NAME });

    const DataTableControls = React.useMemo(
        () =>
            CustomDataTableControls(
                <DataTableDateDropdown
                    setValue={setDateTime}
                    inline
                    afterDate={dateTime.afterDate}
                    beforeDate={dateTime.beforeDate}
                />,
            ),
        [setDateTime],
    );
    const columnHelper = createColumnHelper<StatisticManualPointOpens>();

    const {
        data: manualPointOpens,
        isError,
        isLoading,
    } = useListManualPointOpensStatistics({
        parkingId,
        to_date: dateTime.beforeDate.toISOString(),
        from_date: dateTime.afterDate.toISOString(),
        ...querySortingOptions,
        ...queryPaginationOptions,
        ...queryFiltersOptions,
        ...querySearchOptions,
    });

    const getRowData = React.useMemo(() => {
        const firstSelected = Object.keys(rowSelection.rowSelectionState)[0];
        if (!manualPointOpens || !firstSelected) return;
        return manualPointOpens.results.find((point) => point.id === parseInt(firstSelected));
    }, [manualPointOpens, rowSelection.rowSelectionState]);

    const columns = [
        columnHelper.accessor('operator_name', {
            header: DataTableColumnHeader,
            enableColumnFilter: true,
            meta: {
                title: formatMessage(cardMessages.cardTypeOperator),
                options: users?.results.map((user) => ({ value: user.id, label: user.name })),
            },
            id: 'operator_id',
        }),
        columnHelper.accessor('point_name', {
            header: DataTableColumnHeader,
            enableColumnFilter: true,
            meta: {
                title: formatMessage(statisticsMessages.pointName),
                options: pointData?.results.map((point) => ({ value: point.name, label: point.name })),
            },
        }),
        columnHelper.accessor('event_datetime', {
            header: DataTableColumnHeader,
            cell: CellDatetime,
            enableHiding: true,
            meta: {
                title: formatMessage(statisticsMessages.date),
            },
        }),
        columnHelper.accessor('parking_name', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(statisticsMessages.parkingZone),
            },
        }),
        columnHelper.accessor('open_reason', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(statisticsMessages.openReason),
            },
        }),
    ];

    return (
        <>
            <SectionBlock>
                <Button asChild className="-mt-6 place-self-start p-0" variant="link">
                    <Link to="/statistics/reports">
                        <FontAwesomeIcon icon={faChevronLeft} className="opacity-50" />
                        {formatMessage(statisticsMessages.backToAllReports)}
                    </Link>
                </Button>
                <SectionBlockHeader>
                    <SectionBlockHead>
                        <SectionBlockTitle>{formatMessage(statisticsMessages.manualPointActions)}</SectionBlockTitle>
                        <SectionBlockSubtitle>
                            {formatMessage(statisticsMessages.manualPointActionsSubtitle)}
                        </SectionBlockSubtitle>
                    </SectionBlockHead>
                </SectionBlockHeader>
                <SectionBlockSeparator />
                <DataTable
                    columns={columns}
                    data={manualPointOpens}
                    pagination={pagination}
                    sorting={sorting}
                    filters={filters}
                    search={search}
                    semantics={{
                        title: formatMessage(statisticsMessages.operations),
                        entryName: formatMessage(statisticsMessages.operation),
                        entryNames: formatMessage(statisticsMessages.operations),
                        placeholder: formatMessage(cardMessages.cardTypeOperator),
                        sidePanelName: MANUAL_POINT_OPENS_PANEL_NAME,
                    }}
                    isError={isError}
                    isLoading={isLoading}
                    rowSelection={rowSelection}
                    getRowId={(row) => row.id.toString()}
                    components={{
                        controls: DataTableControls,
                        pagination: DataTablePagination,
                    }}
                />
            </SectionBlock>

            <SidePanel onClose={clearRowSelection} name={MANUAL_POINT_OPENS_PANEL_NAME}>
                <OperationManualPointOpensSidePanel manualPointOpens={getRowData} />
            </SidePanel>
        </>
    );
}
